"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";

const capabilities = [
  { label: "New office & site setup",           detail: "End-to-end fit-out from day one",         icon: "🏢" },
  { label: "Network infrastructure",            detail: "Routers, switches, firewalls, APs",        icon: "🔗" },
  { label: "Data centre & server environments", detail: "Rack design, virtualisation, backups",     icon: "🖥️" },
  { label: "Workstation & endpoint deployment", detail: "Imaging, MDM, asset tagging",              icon: "💻" },
  { label: "POS & EFTPOS system setup",         detail: "Retail-ready, payment-integrated",         icon: "💳" },
  { label: "Multi-site standardisation",        detail: "Consistent configs, zero variance",        icon: "⚙️" },
  { label: "Site-to-site connectivity",         detail: "SD-WAN, MPLS, VPN tunnels",               icon: "📡" },
  { label: "Infrastructure lifecycle planning", detail: "Budgeting, refresh cycles, roadmaps",      icon: "📋" },
];

export default function InfrastructureSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll(".infra-reveal");
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden-reveal");
            entry.target.classList.add("animate-reveal");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items?.forEach((el) => { el?.classList?.add("hidden-reveal", "reveal"); observer?.observe(el); });
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % capabilities?.length);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="pt-16 pb-10 sm:pt-24 sm:pb-10 lg:pt-32 lg:pb-10 relative overflow-hidden"
      style={{ background: "#0F172A" }}
    >
      {/* Background radial */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: "radial-gradient(circle at 80% 20%, rgba(37,99,235,0.14) 0%, transparent 60%)",
        }}
      />
      <div className="absolute inset-0 pointer-events-none grid-overlay" />

      <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center w-full">

          {/* Left: Copy + capability list */}
          <div>
            <div className="infra-reveal stagger-1 section-sep mb-5" />
            <h2
              className="infra-reveal stagger-2 font-heading font-700 leading-tight mb-5"
              style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8FAFC" }}
            >
              Built for businesses
              <br />
              <span className="gradient-text">that span multiple sites.</span>
            </h2>
            <p
              className="infra-reveal stagger-3 text-[16px] font-body leading-relaxed mb-10"
              style={{ color: "#CBD5E1" }}
            >
              From a single new office fit-out to a 100-location retail chain refresh — we&apos;ve done it.
              Our infrastructure team designs, deploys, and documents everything so your IT runs
              the same way at every location.
            </p>

            {/* Capability list */}
            <div className="infra-reveal stagger-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-10">
              {capabilities?.map((cap, i) => (
                <div
                  key={cap?.label}
                  className={`flex items-start gap-3 p-3.5 rounded-xl transition-all duration-300 cursor-default ${
                    activeIdx === i ? "" : ""
                  }`}
                  style={{
                    background: activeIdx === i ? "rgba(37,99,235,0.1)" : "rgba(255,255,255,0.03)",
                    border: activeIdx === i ? "1px solid rgba(59,130,246,0.3)" : "1px solid rgba(255,255,255,0.06)",
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <div
                    className="mt-0.5 shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[16px] transition-all duration-300"
                    style={{
                      background: activeIdx === i ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.05)",
                    }}
                  >
                    {cap?.icon}
                  </div>
                  <div>
                    <div
                      className="font-heading font-500 text-[14px] transition-colors duration-200"
                      style={{ color: activeIdx === i ? "#60A5FA" : "#CBD5E1" }}
                    >
                      {cap?.label}
                    </div>
                    <div className="text-[14px] font-body mt-0.5" style={{ color: "#CBD5E1" }}>
                      {cap?.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="infra-reveal stagger-5 flex flex-wrap gap-3 sm:gap-4">
              <Link
                href="/it-infrastructure"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 text-[13px] sm:text-[14px] font-heading font-600 btn-primary"
              >
                Explore IT Infrastructure
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 text-[13px] sm:text-[14px] font-heading font-500 btn-secondary"
              >
                Get Infrastructure Assessment
              </Link>
            </div>
          </div>

          {/* Right: Visual topology */}
          <div className="infra-reveal stagger-2 relative">
            <div
              className="relative rounded-card-lg overflow-hidden p-8"
              style={{
                background: "rgba(7,11,20,0.8)",
                border: "1px solid rgba(59,130,246,0.2)",
                boxShadow: "0 0 60px rgba(37,99,235,0.1), inset 0 0 60px rgba(37,99,235,0.03)",
                aspectRatio: "4/3",
              }}
            >
              <svg viewBox="0 0 320 240" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="infraGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(59,130,246,0.06)" strokeWidth="0.5"/>
                  </pattern>
                  <radialGradient id="hqNodeGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#60A5FA"/>
                    <stop offset="100%" stopColor="#2563EB"/>
                  </radialGradient>
                  <filter id="nodeGlow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                </defs>

                <rect width="320" height="240" fill="url(#infraGrid)"/>

                {/* Glow behind HQ */}
                <circle cx="160" cy="65" r="35" fill="rgba(37,99,235,0.08)" filter="url(#nodeGlow)"/>

                {/* Connection lines */}
                <line x1="160" y1="65" x2="60" y2="135" stroke="rgba(59,130,246,0.35)" strokeWidth="1" strokeDasharray="4 3" className="network-line"/>
                <line x1="160" y1="65" x2="260" y2="135" stroke="rgba(59,130,246,0.35)" strokeWidth="1" strokeDasharray="4 3" className="network-line"/>
                <line x1="160" y1="65" x2="160" y2="155" stroke="rgba(96,165,250,0.5)" strokeWidth="1.5" strokeDasharray="4 3" className="network-line"/>
                <line x1="60" y1="135" x2="30" y2="205" stroke="rgba(59,130,246,0.2)" strokeWidth="0.8" strokeDasharray="3 3" className="network-line"/>
                <line x1="60" y1="135" x2="100" y2="205" stroke="rgba(59,130,246,0.2)" strokeWidth="0.8" strokeDasharray="3 3" className="network-line"/>
                <line x1="260" y1="135" x2="230" y2="205" stroke="rgba(59,130,246,0.2)" strokeWidth="0.8" strokeDasharray="3 3" className="network-line"/>
                <line x1="260" y1="135" x2="290" y2="205" stroke="rgba(59,130,246,0.2)" strokeWidth="0.8" strokeDasharray="3 3" className="network-line"/>

                {/* HQ node */}
                <circle cx="160" cy="65" r="24" fill="rgba(37,99,235,0.15)" stroke="rgba(59,130,246,0.4)" strokeWidth="1" className="network-pulse"/>
                <circle cx="160" cy="65" r="16" fill="url(#hqNodeGrad)" filter="url(#nodeGlow)"/>
                <text x="160" y="69" textAnchor="middle" fill="white" fontSize="8" fontWeight="700" fontFamily="Sora, sans-serif">HQ</text>
                <text x="160" y="97" textAnchor="middle" fill="rgba(203,213,225,0.75)" fontSize="6.5" fontFamily="Inter, sans-serif">Auckland HQ</text>

                {/* Branch nodes */}
                {[
                  { cx: 60,  cy: 135, label: "Wgtn" },
                  { cx: 260, cy: 135, label: "Chch" },
                  { cx: 160, cy: 155, label: "Hmtn" },
                ]?.map((node) => (
                  <g key={node?.label}>
                    <circle cx={node?.cx} cy={node?.cy} r="17" fill="rgba(37,99,235,0.1)" stroke="rgba(59,130,246,0.3)" strokeWidth="1" className="network-pulse"/>
                    <circle cx={node?.cx} cy={node?.cy} r="11" fill="rgba(59,130,246,0.7)" stroke="rgba(96,165,250,0.4)" strokeWidth="0.5"/>
                    <text x={node?.cx} y={node?.cy + 3.5} textAnchor="middle" fill="white" fontSize="6" fontWeight="600" fontFamily="Sora, sans-serif">{node?.label}</text>
                  </g>
                ))}

                {/* Store nodes */}
                {[
                  { cx: 30,  cy: 205, label: "Store 1" },
                  { cx: 100, cy: 205, label: "Store 2" },
                  { cx: 230, cy: 205, label: "Store 3" },
                  { cx: 290, cy: 205, label: "Store 4" },
                ]?.map((node) => (
                  <g key={node?.label}>
                    <circle cx={node?.cx} cy={node?.cy} r="10" fill="rgba(34,211,238,0.1)" stroke="rgba(34,211,238,0.3)" strokeWidth="0.8"/>
                    <circle cx={node?.cx} cy={node?.cy} r="6" fill="rgba(34,211,238,0.6)"/>
                    <text x={node?.cx} y={node?.cy + 18} textAnchor="middle" fill="rgba(203,213,225,0.7)" fontSize="5.5" fontFamily="Inter, sans-serif">{node?.label}</text>
                  </g>
                ))}

                {/* Legend */}
                <g transform="translate(8, 8)">
                  <circle cx="6" cy="6" r="4" fill="#3B82F6"/>
                  <text x="14" y="10" fill="rgba(203,213,225,0.75)" fontSize="6" fontFamily="Inter, sans-serif">Headquarters</text>
                  <circle cx="6" cy="20" r="4" fill="rgba(59,130,246,0.7)"/>
                  <text x="14" y="24" fill="rgba(203,213,225,0.75)" fontSize="6" fontFamily="Inter, sans-serif">Regional Office</text>
                  <circle cx="6" cy="34" r="4" fill="rgba(34,211,238,0.6)"/>
                  <text x="14" y="38" fill="rgba(203,213,225,0.75)" fontSize="6" fontFamily="Inter, sans-serif">Branch / Store</text>
                </g>
              </svg>

              {/* Overlay stat */}
              <div
                className="absolute bottom-5 right-5 rounded-xl px-4 py-3"
                style={{
                  background: "rgba(7,11,20,0.9)",
                  border: "1px solid rgba(59,130,246,0.3)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <div className="font-heading font-700 text-[22px] leading-none" style={{ color: "#60A5FA" }}>200+</div>
                <div className="text-[11px] font-body" style={{ color: "rgba(148,163,184,0.6)" }}>Sites Deployed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}